/**
 * TanStack Query Client Configuration
 * Centralized query client with default options and global error handling
 */

import { QueryClient, MutationCache } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getMutationError } from '@/lib/utils/get-mutation-error';

/**
 * Mutation metadata for global error handling
 * Pass via `meta` property in useMutation options
 *
 * @example
 * useMutation({
 *   mutationFn: ...,
 *   meta: { operation: 'create', entity: 'cliente' },
 *   onSuccess: () => toast.success('Cliente creado'),
 * })
 */
export interface MutationMeta {
  operation: 'create' | 'update' | 'delete' | 'fetch' | 'generate' | 'copy' | 'assign' | 'remove';
  entity: string;
  /** Skip global error toast (useful if handling errors locally) */
  skipGlobalError?: boolean;
}

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      const meta = mutation.meta as MutationMeta | undefined;

      // Skip if no meta or explicitly disabled
      if (!meta?.operation || !meta?.entity || meta.skipGlobalError) return;

      const message = getMutationError(error, {
        operation: meta.operation,
        entity: meta.entity,
      });

      toast.error(message);
    },
  }),
  defaultOptions: {
    queries: {
      // Stale time: data is considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000,

      // Cache time: inactive data kept in cache for 10 minutes
      gcTime: 10 * 60 * 1000,

      // Retry failed requests 1 time
      retry: 1,

      // Retry delay: exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetch on window focus (useful for real-time updates)
      refetchOnWindowFocus: true,

      // Refetch on reconnect
      refetchOnReconnect: true,

      // Don't refetch on mount if data is fresh
      refetchOnMount: true,
    },
    mutations: {
      // Retry failed mutations 0 times (user-initiated actions)
      retry: 0,
    },
  },
});

/**
 * Query Keys Factory
 * Centralized query key management for cache invalidation
 */
export const queryKeys = {
  // Auth
  auth: {
    profile: (userId: number) => ['auth', 'profile', userId] as const,
    users: () => ['auth', 'users'] as const,
  },

  // Evaluaciones
  evaluaciones: {
    all: () => ['evaluaciones'] as const,
    list: (filters?: import('@/types/api').EvaluacionRiesgoListFilters) =>
      filters ? (['evaluaciones', 'list', filters] as const) : (['evaluaciones'] as const),
    detail: (id: number) => ['evaluaciones', id] as const,
  },

  // Zonas
  zonas: {
    all: () => ['zonas'] as const,
    byEvaluacion: (evaluacionId: number) => ['zonas', 'evaluacion', evaluacionId] as const,
    detail: (id: number) => ['zonas', id] as const,
  },

  // Peligros
  peligros: {
    all: () => ['peligros'] as const,
    byZona: (
      zonaId: number,
      filters?: Partial<Omit<import('@/types/api').GetPeligrosParams, 'idZona'>>
    ) => ['peligros', 'byZona', zonaId, filters] as const,
    byZonaPrefix: (zonaId: number) => ['peligros', 'byZona', zonaId] as const,
    detail: (id: number) => ['peligros', id] as const,
  },

  // Clientes
  clientes: {
    all: () => ['clientes'] as const,
    filtered: (filters?: import('@/types/api').ClientesFilters) =>
      filters ? (['clientes', 'filtered', filters] as const) : (['clientes'] as const),
    detail: (id: number) => ['clientes', id] as const,
    plantasCriticas: (idCliente: number) => ['clientes', idCliente, 'plantas-criticas'] as const,
    maquinasCriticas: (idCliente: number, planta: string) =>
      ['clientes', idCliente, 'maquinas-criticas', planta] as const,
  },

  // Máquinas
  maquinas: {
    all: () => ['maquinas'] as const,
    list: (filters?: import('@/types/api').MaquinasListFilters) =>
      filters ? (['maquinas', 'list', filters] as const) : (['maquinas'] as const),
    detail: (id: number) => ['maquinas', id] as const,
    byCliente: (clienteId: number) => ['maquinas', 'cliente', clienteId] as const,
  },

  // Usuarios
  usuarios: {
    all: () => ['usuarios'] as const,
    detail: (id: number) => ['usuarios', id] as const,
  },

  // Tareas
  tareas: {
    all: () => ['tareas'] as const,
    detail: (id: number) => ['tareas', id] as const,
    byZona: (zonaId: number) => ['tareas', 'zona', zonaId] as const,
  },

  // Status Evaluación
  statusEvaluacion: {
    all: () => ['status-evaluacion'] as const,
    detail: (id: number) => ['status-evaluacion', id] as const,
  },

  // Tipo Cliente
  tipoCliente: {
    all: () => ['tipo-clientes'] as const,
    detail: (id: number) => ['tipo-clientes', id] as const,
  },

  // Tipo Máquina
  tipoMaquina: {
    all: () => ['tipo-maquinas'] as const,
    detail: (id: number) => ['tipo-maquinas', id] as const,
  },

  // Tipo Peligro
  tipoPeligro: {
    all: () => ['tipo-peligro'] as const,
    list: (params?: { skip?: number; limit?: number }) => ['tipo-peligro', 'list', params] as const,
    detail: (id: number) => ['tipo-peligro', id] as const,
  },

  // Normas ISO
  normasIso: {
    all: () => ['normas-iso'] as const,
    list: (params?: unknown) => ['normas-iso', 'list', params] as const,
    detail: (id: number) => ['normas-iso', id] as const,
  },

  // Medidas de Control (zona-specific instances)
  medidas: {
    all: () => ['medidas-control'] as const,
    detail: (id: number) => ['medidas-control', id] as const,
    byZona: (zonaId: number) => ['medidas-control', 'zona', zonaId] as const,
    byZonaAndCodigo: (zonaId: number, codigo: 'DI' | 'MP' | 'IU') =>
      ['medidas-control', 'zona', zonaId, 'codigo', codigo] as const,
  },

  // Template Medidas de Control (generic templates)
  templateMedidas: {
    all: () => ['template-medidas-control'] as const,
    list: (params?: unknown) => ['template-medidas-control', 'list', params] as const,
    detail: (id: number) => ['template-medidas-control', id] as const,
  },

  // Límites de Evaluación
  limitesEvaluacion: {
    all: () => ['limites-evaluacion'] as const,
    detail: (id: number) => ['limites-evaluacion', id] as const,
    byEvaluacion: (evaluacionId: number) =>
      ['limites-evaluacion', 'evaluacion', evaluacionId] as const,
  },

  // Documentos
  documentos: {
    all: () => ['documentos'] as const,
    detail: (id: number) => ['documentos', id] as const,
    byEvaluacion: (evaluacionId: number) => ['documentos', 'evaluacion', evaluacionId] as const,
  },

  // Maquinas-Clientes (assignments)
  maquinasClientes: {
    all: () => ['maquinas-clientes'] as const,
    detail: (id: number) => ['maquinas-clientes', id] as const,
    byClient: (clientId: number) => ['maquinas-clientes', 'client', clientId] as const,
  },

  // Comentarios (Evaluation comments)
  comentarios: {
    all: () => ['comentarios'] as const,
    detail: (id: number) => ['comentarios', id] as const,
    byEvaluacion: (evaluacionId: number) => ['comentarios', 'evaluacion', evaluacionId] as const,
  },

  // Tareas Zona (zone task assignments)
  tareasZona: {
    all: () => ['tareas-zona'] as const,
    detail: (id: number) => ['tareas-zona', id] as const,
    byZona: (zonaId: number) => ['tareas-zona', 'zona', zonaId] as const,
  },

  // Medidas-Peligros Grouping (riesgo residual evaluations)
  medidasPeligrosGrouping: {
    all: () => ['medidas-peligros-grouping'] as const,
    byZona: (zonaPeligroId: number) =>
      ['medidas-peligros-grouping', 'zona', zonaPeligroId] as const,
    group: (groupId: number) => ['medidas-peligros-grouping', 'group', groupId] as const,
  },

  // Contactos Cliente
  contactosCliente: {
    all: () => ['contactos-cliente'] as const,
    detail: (id: number) => ['contactos-cliente', id] as const,
    byCliente: (clienteId: number) => ['contactos-cliente', 'cliente', clienteId] as const,
  },

  // Intervenciones
  intervenciones: {
    all: () => ['intervenciones'] as const,
    detail: (id: number) => ['intervenciones', id] as const,
    byEvaluacion: (evaluacionId: number) => ['intervenciones', 'evaluacion', evaluacionId] as const,
  },

  // Definiciones Intervenciones (child of intervenciones)
  definicionesIntervenciones: {
    all: () => ['definiciones-intervenciones'] as const,
    detail: (id: number) => ['definiciones-intervenciones', id] as const,
    byIntervencion: (intervencionId: number) =>
      ['definiciones-intervenciones', 'intervencion', intervencionId] as const,
  },

  // Historia Revisiones
  historiaRevisiones: {
    all: () => ['historia-revisiones'] as const,
    detail: (id: number) => ['historia-revisiones', id] as const,
    byEvaluacion: (evaluacionId: number) =>
      ['historia-revisiones', 'evaluacion', evaluacionId] as const,
  },

  // Result Evaluacion
  resultEvaluacion: {
    all: () => ['result-evaluacion'] as const,
    detail: (id: number) => ['result-evaluacion', id] as const,
    byEvaluacion: (evaluacionId: number) =>
      ['result-evaluacion', 'evaluacion', evaluacionId] as const,
  },

  // Dashboard Statistics
  dashboard: {
    byMonth: (year?: number) => ['dashboard', 'evaluaciones-por-mes', year] as const,
    byStatus: (year?: number, month?: number) =>
      ['dashboard', 'evaluaciones-por-status', year, month] as const,
    hazardTotals: (year?: number, month?: number) =>
      ['dashboard', 'totales-peligros', year, month] as const,
  },

  // Levantamientos
  levantamientos: {
    all: () => ['levantamientos'] as const,
    filtered: (filters?: import('@/types/api').LevantamientoFilters) =>
      filters ? (['levantamientos', 'filtered', filters] as const) : (['levantamientos'] as const),
    detail: (id: number) => ['levantamientos', id] as const,
    byPlanta: (plantaId: number) => ['levantamientos', 'planta', plantaId] as const,
    complianceByLine: (id: number) => ['levantamientos', id, 'compliance-by-line'] as const,
    compliancePlanta: (id: number) => ['levantamientos', id, 'compliance-planta'] as const,
    machinesByLine: (id: number) => ['levantamientos', id, 'machines-by-line'] as const,
  },

  // Plantas
  plantas: {
    all: () => ['plantas'] as const,
    lists: () => ['plantas', 'list'] as const,
    list: (filters?: import('@/types/api').PlantaFilters) => ['plantas', 'list', filters] as const,
    details: () => ['plantas', 'detail'] as const,
    detail: (id: number) => ['plantas', 'detail', id] as const,
  },

  // Lineas
  lineas: {
    all: () => ['lineas'] as const,
    lists: () => ['lineas', 'list'] as const,
    list: (filters?: import('@/types/api').LineaFilters) => ['lineas', 'list', filters] as const,
    details: () => ['lineas', 'detail'] as const,
    detail: (id: number) => ['lineas', 'detail', id] as const,
    byPlanta: (idPlanta: number) => ['lineas', 'by-planta', idPlanta] as const,
    selectOptions: (idPlanta: number) => ['lineas', 'select-options', idPlanta] as const,
  },

  // SubMaquinas
  subMaquinas: {
    all: () => ['sub-maquinas'] as const,
    detail: (id: number) => ['sub-maquinas', id] as const,
    byLinea: (lineaId: number) => ['sub-maquinas', 'linea', lineaId] as const,
    byPlanta: (plantaId: number) => ['sub-maquinas', 'planta', plantaId] as const,
  },

  // Secciones
  secciones: {
    all: () => ['secciones'] as const,
    detail: (id: number) => ['secciones', id] as const,
    bySubMaquina: (subMaquinaId: number) => ['secciones', 'sub-maquina', subMaquinaId] as const,
  },

  // Cuestionamientos
  cuestionamientos: {
    all: () => ['cuestionamientos'] as const,
    detail: (id: number) => ['cuestionamientos', id] as const,
    bySeccion: (seccionId: number) => ['cuestionamientos', 'seccion', seccionId] as const,
  },

  // Templates: Psecciones (Section Templates)
  psecciones: {
    all: () => ['psecciones'] as const,
    filtered: (filters?: import('@/types/api').PseccionFilters) =>
      filters ? (['psecciones', 'filtered', filters] as const) : (['psecciones'] as const),
    detail: (id: number) => ['psecciones', id] as const,
  },

  // Templates: CuestionamientosP (Question Templates)
  cuestionamientosP: {
    all: () => ['cuestionamientos-p'] as const,
    filtered: (filters?: import('@/types/api').CuestionamientoPFilters) =>
      filters
        ? (['cuestionamientos-p', 'filtered', filters] as const)
        : (['cuestionamientos-p'] as const),
    detail: (id: number) => ['cuestionamientos-p', id] as const,
    byPseccion: (pseccionId: number) => ['cuestionamientos-p', 'pseccion', pseccionId] as const,
  },

  // Informes (PDF Reports)
  informes: {
    all: () => ['informes'] as const,
    byEvaluacion: (evaluacionId: number, filters?: Record<string, unknown>) =>
      ['informes', 'evaluacion', evaluacionId, filters] as const,
  },
} as const;
